import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, Banknote, BellRing, CheckCircle2, Clock, Loader2, Pencil, Plus, Printer as PrinterIcon, Radar, Trash2, X, XCircle,
} from 'lucide-react';
import ConfirmModal from '../shared/ConfirmModal';
import { useToast } from '../shared/Toast';
import { SheetSection } from '../settings/sheet';
import PrinterForm from './PrinterForm';
import { DiscoveredQueue, PrintingAgent, PrintingPrinter, TestPrintResult, printingApi } from './printingApi';
import { PrinterPrefill, activePrinters, receiptPrinters } from './printingModel';
import { ASIDE_BTN, GHOST_BTN, ICON_BTN, StatusDot, Tag } from './printingUi';

/**
 * «Εκτυπωτές»: η λίστα (ουρά Windows, ρόλοι, χαρτί, συρτάρι, μπιπ, κατάσταση, «Δοκιμή» με το αποτέλεσμα
 * του agent) και «Ανακάλυψη» → προσθήκη με ένα κλικ. Κάθε αλλαγή εδώ γράφεται αμέσως· η σελίδα
 * ξαναδιαβάζει μετά (onChanged). Ο «Κύριος εκτυπωτής αποδείξεων» διαλέγεται από 25/10/2026 στο «Πού
 * βγαίνει κάθε χαρτί» (ένα σημείο για το πού τυπώνεται τι)· εδώ φαίνεται μόνο το «· κύριος».
 */

interface Props {
  storeId: number;
  printers: PrintingPrinter[];
  agent: PrintingAgent;
  onChanged: () => Promise<void> | void;
}

type Discovery =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'ready'; queues: DiscoveredQueue[] }
  | { state: 'error'; message: string };

type TestState = { state: 'busy' } | { state: 'done'; result: TestPrintResult } | { state: 'error'; message: string };

type Editing = { printer: PrintingPrinter | null; prefill?: PrinterPrefill; key: number };

const PrintersSection: React.FC<Props> = ({ storeId, printers, agent, onChanged }) => {
  const { t, i18n } = useTranslation(['printing']);
  const { showToast } = useToast();
  const [discovery, setDiscovery] = useState<Discovery>({ state: 'idle' });
  const [editing, setEditing] = useState<Editing | null>(null);
  const [tests, setTests] = useState<Record<number, TestState>>({});
  const [deleting, setDeleting] = useState<PrintingPrinter | null>(null);

  const list = activePrinters(printers);
  const receipts = receiptPrinters(printers);
  const nameOf = (id: number | null) => printers.find(p => p.id === id)?.name || null;
  // Μόλις προστέθηκε από την ανακάλυψη: «ήδη στη λίστα» χωρίς δεύτερη ερώτηση στον agent (ως 15″).
  const sameQueue = (queue: string) => list.find(p => (p.windows_printer_name || '').trim().toLowerCase() === queue.trim().toLowerCase());
  const queues = discovery.state === 'ready' ? discovery.queues.map(q => q.windows_printer_name) : [];
  const nf = (n: number, digits = 0) => new Intl.NumberFormat(i18n.language || 'el', { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(n);

  const openEditor = (printer: PrintingPrinter | null, prefill?: PrinterPrefill) =>
    setEditing({ printer, prefill, key: Date.now() });

  const discover = async () => {
    setDiscovery({ state: 'loading' });
    const r = await printingApi.discover(storeId);
    if (!r.ok || !r.data) {
      setDiscovery({ state: 'error', message: r.error || t('printing:discover.failed') });
      return;
    }
    setDiscovery({ state: 'ready', queues: r.data });
  };

  const test = async (printer: PrintingPrinter) => {
    setTests(prev => ({ ...prev, [printer.id]: { state: 'busy' } }));
    const r = await printingApi.testPrint(storeId, printer.id);
    setTests(prev => ({
      ...prev,
      [printer.id]: r.ok && r.data
        ? { state: 'done', result: r.data }
        : { state: 'error', message: r.error || (r.status === 0 ? t('printing:form.network') : t('printing:printers.testError')) },
    }));
  };

  const remove = async () => {
    const printer = deleting;
    setDeleting(null);
    if (!printer) return;
    const r = await printingApi.deletePrinter(storeId, printer.id);
    if (!r.ok) {
      showToast('error', r.error || t('printing:printers.deleteFailed'));
      return;
    }
    showToast('success', t('printing:printers.deleted', { name: printer.name }));
    await onChanged();
  };

  const testLine = (printer: PrintingPrinter) => {
    const state = tests[printer.id];
    if (!state || state.state === 'busy') return null;
    let tone = 'text-red-600 dark:text-red-400';
    let icon = <XCircle className="w-4 h-4 shrink-0" aria-hidden="true" />;
    let message: string;
    if (state.state === 'error') {
      message = t('printing:printers.testNotSent', { error: state.message });
    } else if (state.result.status === 'completed') {
      tone = 'text-emerald-700 dark:text-emerald-400';
      icon = <CheckCircle2 className="w-4 h-4 shrink-0" aria-hidden="true" />;
      message = state.result.waited_ms != null
        ? t('printing:printers.testCompletedIn', { seconds: nf(state.result.waited_ms / 1000, 1) })
        : t('printing:printers.testCompleted');
    } else if (state.result.status === 'failed') {
      message = state.result.error
        ? t('printing:printers.testFailed', { error: state.result.error })
        : t('printing:printers.testFailedNoReason');
    } else {
      // Ο server λέει γιατί (agent εκτός σύνδεσης / δεν απάντησε σε 10″)· αλλιώς το γενικό.
      tone = 'text-amber-800 dark:text-amber-300';
      icon = <Clock className="w-4 h-4 shrink-0" aria-hidden="true" />;
      message = state.result.error || t('printing:printers.testPending');
    }
    return (
      <p className={`mt-2 flex items-start gap-1.5 text-[13px] leading-[19px] ${tone}`} data-testid={`test-result-${printer.id}`}>
        {icon}<span className="min-w-0">{message}</span>
        <button type="button" className="ml-1 -my-1 w-7 h-7 rounded-lg grid place-items-center text-t4 hover:text-t1 hover:bg-surface-3"
          aria-label={t('printing:printers.testDismiss')} onClick={() => setTests(prev => {
            const next = { ...prev };
            delete next[printer.id];
            return next;
          })}>
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </p>
    );
  };

  const row = (p: PrintingPrinter) => {
    const testing = tests[p.id]?.state === 'busy';
    return (
      <li key={p.id} data-testid={`printer-row-${p.id}`} className="py-4 first:pt-0 last:pb-0">
        <div className="flex items-start gap-3">
          <PrinterIcon className="w-5 h-5 mt-0.5 shrink-0 text-t3" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm font-semibold text-t1 break-words">{p.name}</span>
              <span className="inline-flex items-center gap-1.5 text-xs text-t3">
                <StatusDot on={p.agent_online} />
                {p.agent_online ? t('printing:printers.online') : t('printing:printers.offline')}
              </span>
            </div>
            {p.windows_printer_name
              ? <p className="mt-0.5 text-xs font-mono text-t3 break-all">{p.windows_printer_name}</p>
              : (
                <p className="mt-0.5 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                  <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />{t('printing:printers.noQueue')}
                </p>
              )}
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`printer-tags-${p.id}`}>
              {p.prints_receipts && (
                <Tag strong>
                  {t('printing:roles.receipts')}
                  {p.is_default_receipt && receipts.length > 1 && <span className="font-normal">· {t('printing:printers.main')}</span>}
                </Tag>
              )}
              {p.prints_tickets && (
                <Tag strong>{t('printing:roles.tickets')} · {p.ticket_style === 'bar' ? t('printing:styles.bar') : t('printing:styles.kitchen')}</Tag>
              )}
              <Tag>{t('printing:paper', { width: p.paper_width })}</Tag>
              {p.prints_receipts && p.cash_drawer !== 'off' && <Tag><Banknote className="w-3.5 h-3.5" aria-hidden="true" />{t('printing:printers.drawer')}</Tag>}
              {p.prints_tickets && p.beep_enabled && <Tag><BellRing className="w-3.5 h-3.5" aria-hidden="true" />{t('printing:printers.beep')}</Tag>}
            </div>
            {(p.pending_jobs > 0 || p.jobs_today > 0) && (
              <p className="mt-2 text-xs text-t3">
                {p.pending_jobs > 0 && (
                  <span className="font-medium text-amber-800 dark:text-amber-300">{t('printing:printers.pending', { count: p.pending_jobs })}</span>
                )}
                {p.pending_jobs > 0 && p.jobs_today > 0 && ' · '}
                {p.jobs_today > 0 && t('printing:printers.today', { count: p.jobs_today })}
              </p>
            )}
            {testLine(p)}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button type="button" onClick={() => test(p)} disabled={testing} aria-busy={testing}
              aria-label={t('printing:printers.testAria', { name: p.name })}
              className={`${GHOST_BTN} min-h-[40px] px-3`}>
              {testing && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              <span aria-hidden="true">{testing ? t('printing:printers.testing') : t('printing:printers.test')}</span>
            </button>
            <button type="button" onClick={() => openEditor(p)} aria-label={t('printing:printers.editAria', { name: p.name })} className={ICON_BTN}>
              <Pencil className="w-4 h-4" aria-hidden="true" />
            </button>
            <button type="button" onClick={() => setDeleting(p)} aria-label={t('printing:printers.deleteAria', { name: p.name })}
              className={`${ICON_BTN} hover:text-red-600 dark:hover:text-red-400`}>
              <Trash2 className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </li>
    );
  };

  const discoveryPanel = () => {
    if (discovery.state === 'idle') return null;
    return (
      <div className="mb-5 rounded-xl border border-line bg-surface-2 px-4 py-3" data-testid="discovery">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-semibold text-t1">{t('printing:discover.title')}</p>
          {discovery.state !== 'loading' && (
            <button type="button" onClick={() => setDiscovery({ state: 'idle' })} aria-label={t('printing:discover.close')}
              className="-mr-2 -mt-1 w-9 h-9 rounded-lg grid place-items-center text-t3 hover:text-t1 hover:bg-surface-3">
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>
        {discovery.state === 'loading' && (
          <p role="status" className="mt-2 flex items-center gap-2 text-sm text-t3">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />{t('printing:discover.searching')}
          </p>
        )}
        {discovery.state === 'error' && (
          <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-400">{discovery.message}</p>
        )}
        {discovery.state === 'ready' && (discovery.queues.length === 0 ? (
          <p className="mt-2 text-sm text-t3">{t('printing:discover.none')}</p>
        ) : (
          <>
            <p className="mt-0.5 text-xs text-t3">{t('printing:discover.found', { count: discovery.queues.length })}</p>
            <ul className="mt-2 divide-y divide-line">
              {discovery.queues.map(q => {
                const addedId = q.already_added ?? sameQueue(q.windows_printer_name)?.id ?? null;
                const existing = addedId != null ? nameOf(addedId) : null;
                return (
                  <li key={q.windows_printer_name} className="flex items-center justify-between gap-3 py-2" data-testid="discovered-queue">
                    <span className="min-w-0 text-sm font-mono text-t1 break-all">{q.windows_printer_name}</span>
                    {addedId != null ? (
                      <span className="shrink-0 text-xs text-t3">
                        {existing ? t('printing:discover.addedAs', { name: existing }) : t('printing:discover.added')}
                      </span>
                    ) : (
                      <button type="button" className={`${GHOST_BTN} shrink-0 min-h-[36px] px-3`}
                        aria-label={t('printing:discover.addAria', { name: q.windows_printer_name })}
                        onClick={() => openEditor(null, { name: q.windows_printer_name, windows_printer_name: q.windows_printer_name })}>
                        <Plus className="w-4 h-4" aria-hidden="true" />{t('printing:discover.add')}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        ))}
      </div>
    );
  };

  return (
    <SheetSection
      id="printing-printers"
      title={t('printing:printers.title')}
      hint={t('printing:printers.hint')}
      testId="printers-section"
      aside={(
        <div className="flex flex-col items-start gap-2">
          <button type="button" onClick={discover} disabled={discovery.state === 'loading'} className={ASIDE_BTN}>
            {discovery.state === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Radar className="w-4 h-4" aria-hidden="true" />}
            {t('printing:printers.discover')}
          </button>
          <button type="button" onClick={() => openEditor(null)} className={ASIDE_BTN}>
            <Plus className="w-4 h-4" aria-hidden="true" />{t('printing:printers.add')}
          </button>
        </div>
      )}
    >
      {discoveryPanel()}

      {list.length === 0 ? (
        <p className="text-sm text-t3">{t('printing:printers.empty')}</p>
      ) : (
        <ul className="divide-y divide-line" aria-label={t('printing:printers.listAria')}>{list.map(row)}</ul>
      )}


      {editing && (
        <PrinterForm
          key={editing.key}
          storeId={storeId}
          printer={editing.printer}
          prefill={editing.prefill}
          printers={printers}
          agent={agent}
          queues={queues}
          onClose={() => setEditing(null)}
          onSaved={(name, created) => {
            setEditing(null);
            showToast('success', created ? t('printing:printers.added', { name }) : t('printing:printers.saved', { name }));
            onChanged();
          }}
        />
      )}

      <ConfirmModal
        isOpen={deleting !== null}
        title={t('printing:printers.deleteTitle', { name: deleting?.name || '' })}
        message={t('printing:printers.deleteMessage')}
        confirmLabel={t('printing:printers.deleteConfirm')}
        cancelLabel={t('printing:cancel')}
        variant="danger"
        onConfirm={remove}
        onCancel={() => setDeleting(null)}
      />
    </SheetSection>
  );
};

export default PrintersSection;
